import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  TimeScale,
} from 'chart.js';
import 'chartjs-adapter-date-fns';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  TimeScale
);

export default function EloGraph({ matches, decks, deckId, getMatchDisplayData, xAxisType = 'date' }) {
  // Filtrer les decks selon le deck sélectionné (ou tous)
  const filteredDecks = deckId && deckId !== 'all'
    ? decks.filter(deck => deck.id === deckId)
    : decks;

  // Génère une couleur unique par deck (si deck.color absent)
  function getDeckColor(idx) {
    // Palette de couleurs pastels
    const palette = [
      '#e6194b', '#3cb44b', '#ffe119', '#4363d8', '#f58231', '#911eb4', '#46f0f0', '#f032e6',
      '#bcf60c', '#fabebe', '#008080', '#e6beff', '#9a6324', '#fffac8', '#800000', '#aaffc3',
      '#808000', '#ffd8b1', '#000075', '#808080', '#ffffff', '#000000'
    ];
    return palette[idx % palette.length];
  }

  // Pour chaque deck, construire l'évolution de l'ELO dans le temps
  const datasets = useMemo(() => {
    return filteredDecks.map((deck, idx) => {
      let points = [];
      // On part de l'initial ELO, puis on parcourt les matchs dans l'ordre chronologique
      const deckMatches = matches
        .map(m => getMatchDisplayData(m, decks))
        .filter(m => m.deck1.id === deck.id || m.deck2.id === deck.id)
        .sort((a, b) => new Date(a.date) - new Date(b.date));
      let currentElo = deckMatches.length > 0 ? deckMatches[0][deckMatches[0].deck1.id === deck.id ? 'deck1' : 'deck2'].oldElo : deck.elo;
      if (xAxisType === 'date') {
        points.push({ x: deckMatches[0] ? new Date(deckMatches[0].date) : new Date(), y: currentElo });
        deckMatches.forEach(match => {
          const d = match.deck1.id === deck.id ? match.deck1 : match.deck2;
          points.push({ x: new Date(match.date), y: d.newElo });
        });
      } else {
        // x = numéro de match (1, 2, ...)
        points.push({ x: 0, y: currentElo });
        deckMatches.forEach((match, i) => {
          const d = match.deck1.id === deck.id ? match.deck1 : match.deck2;
          points.push({ x: i + 1, y: d.newElo });
        });
      }
      return {
        label: deck.name,
        data: points,
        fill: false,
        borderColor: deck.color || getDeckColor(idx),
        backgroundColor: deck.color || getDeckColor(idx),
        tension: 0.2,
      };
    });
  }, [filteredDecks, matches, decks, getMatchDisplayData, deckId, xAxisType]);

  const data = {
    datasets,
  };

  const options = {
    responsive: true,
    plugins: {
      legend: { display: true },
      tooltip: { mode: 'index', intersect: false },
    },
    scales: {
      x: xAxisType === 'date'
        ? {
            type: 'time',
            time: { unit: 'day' },
            title: { display: true, text: 'Date' },
          }
        : {
            type: 'linear',
            title: { display: true, text: 'Numéro de match' },
            ticks: { stepSize: 1, precision: 0 },
          },
      y: {
        title: { display: true, text: 'ELO' },
        beginAtZero: false,
      },
    },
  };

  return (
    <div className="p-4">
      {datasets.length === 0 ? (
        <div className="text-gray-400 text-center">Aucune donnée pour ce deck.</div>
      ) : (
        <Line data={data} options={options} />
      )}
    </div>
  );
}
